import React from 'react';
import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  getListingInputModalDialogProps,
  LISTING_INPUT_MODAL_CONTENT_SX,
} from './listingInputModalStyles';
import {
  ListingModalSection,
  ListingModalTitleBar,
  LISTING_MODAL_BODY_SX,
} from './listingModalUi';

/**
 * Shared confirmation dialog for unlinking a floor plan from a listing.
 *
 * Used from both the Listings section (row-level Unlink button) and the Floor
 * plans library (row-level Unlink button) so partners always see the same
 * cautionary modal regardless of where they triggered the action from.
 */
const UnlinkConfirmDialog = ({
  open,
  plan = null,
  listingRef = '',
  onCancel,
  onConfirm,
  pending = false,
}) => {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const dialogProps = getListingInputModalDialogProps(fullScreenDialog);

  return (
    <Dialog
      open={Boolean(open)}
      onClose={pending ? undefined : onCancel}
      {...dialogProps}
    >
      <ListingModalTitleBar
        title="Unlink listing?"
        subtitle={listingRef ? `Listing ${listingRef}` : null}
        onClose={onCancel}
        onSave={onConfirm}
        saving={pending}
        saveAriaLabel="Unlink listing"
        destructive
      />
      <DialogContent sx={LISTING_INPUT_MODAL_CONTENT_SX}>
        <Stack spacing={2.5}>
          <ListingModalSection>
            {plan ? (
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{
                  p: 1,
                  borderRadius: '10px',
                  bgcolor: 'rgba(0,0,0,0.03)',
                  border: '1px solid rgba(0,0,0,0.06)',
                  mb: 1.5,
                }}
              >
                <Avatar
                  variant="rounded"
                  src={plan.thumbnail || undefined}
                  sx={{ width: 40, height: 40, borderRadius: '8px', flexShrink: 0 }}
                >
                  {plan.name?.[0] || 'F'}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }} noWrap>
                    {plan.name || 'Floor plan'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'rgba(0,0,0,0.5)' }} noWrap>
                    Listing {listingRef}
                  </Typography>
                </Box>
              </Stack>
            ) : null}
            <Typography sx={{ ...LISTING_MODAL_BODY_SX, color: 'rgba(0,0,0,0.6)' }}>
              Listing {listingRef} will no longer be linked to this floor plan. Past activity stays in
              Recent Activity; this row will disappear from Listings until you link again.
            </Typography>
          </ListingModalSection>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default UnlinkConfirmDialog;
